/**
 * Loading Context - Tracks multiple loading operations with progress
 */

import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export interface LoadingOperation {
  id: string;
  status: string;
  progress: number; // 0-100
  weight: number; // How much this operation contributes to overall progress
}

interface LoadingContextType {
  // State
  operations: LoadingOperation[];
  isLoading: boolean;
  overallProgress: number;
  currentStatus: string;
  
  // Actions
  startOperation: (id: string, status: string, weight?: number) => void;
  updateOperation: (id: string, status: string, progress: number) => void;
  completeOperation: (id: string) => void;
  clearAllOperations: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export function useLoading() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
}

interface LoadingProviderProps {
  children: ReactNode;
}

export function LoadingProvider({ children }: LoadingProviderProps) {
  const [operations, setOperations] = useState<LoadingOperation[]>([]);

  const startOperation = useCallback((id: string, status: string, weight: number = 1) => {
    setOperations(prev => {
      // Remove existing operation with same ID if it exists
      const filtered = prev.filter(op => op.id !== id);
      return [...filtered, { id, status, progress: 0, weight }];
    });
  }, []);

  const updateOperation = useCallback((id: string, status: string, progress: number) => {
    setOperations(prev => 
      prev.map(op => 
        op.id === id 
          ? { ...op, status, progress: Math.min(100, Math.max(0, progress)) }
          : op
      )
    );
  }, []);

  const completeOperation = useCallback((id: string) => {
    setOperations(prev => prev.filter(op => op.id !== id));
  }, []);

  const clearAllOperations = useCallback(() => {
    setOperations([]);
  }, []);

  // Calculate overall progress and current status
  const isLoading = operations.length > 0;
  
  const overallProgress = React.useMemo(() => {
    if (operations.length === 0) return 0;
    
    const totalWeight = operations.reduce((sum, op) => sum + op.weight, 0);
    if (totalWeight === 0) return 0;
    
    const weightedProgress = operations.reduce((sum, op) => {
      return sum + (op.progress * op.weight);
    }, 0);
    
    return weightedProgress / totalWeight;
  }, [operations]);

  const currentStatus = React.useMemo(() => {
    if (operations.length === 0) return '';
    
    // Find the operation with the lowest progress (currently active)
    const activeOperation = operations.reduce((min, op) => 
      op.progress < min.progress ? op : min
    );
    
    return activeOperation.status;
  }, [operations]);

  const value: LoadingContextType = {
    operations,
    isLoading,
    overallProgress,
    currentStatus,
    startOperation,
    updateOperation,
    completeOperation,
    clearAllOperations,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
}

// Predefined operation IDs and weights for consistency
export const LOADING_OPERATIONS = {
  // Single unified operation for the entire data loading flow
  DATA_FLOW: {
    id: 'data-flow',
    weight: 1,
    statuses: {
      // 0-30%: Directus data fetching
      FETCHING_FREEDOM_GOALS: 'Fetching freedom goals...',
      FETCHING_ASSETS: 'Fetching assets...',
      FETCHING_TRANSACTIONS: 'Fetching transactions...',
      FETCHING_STOCK_DATA: 'Fetching stock/ETF data...',
      FETCHING_CRYPTO_DATA: 'Fetching crypto data...',
      FETCHING_SAVINGS_DATA: 'Fetching savings data...',
      FETCHING_REAL_ESTATE_DATA: 'Fetching real estate data...',
      FETCHING_MORTGAGE_DATA: 'Fetching mortgage data...',
      FETCHING_LOAN_DATA: 'Fetching loan data...',
      FETCHING_RENTAL_DATA: 'Fetching rental data...',
      
      // 30-40%: Metadata enrichment
      ENRICHING_METADATA: 'Enriching asset metadata...',
      
      // 40-60%: Market data
      FETCHING_MARKET_QUOTES: 'Getting latest market prices...',
      
      // 60-70%: Position calculations
      CALCULATING_POSITIONS: 'Calculating positions...',
      
      // 70-85%: Historical data
      FETCHING_HISTORICAL: 'Loading historical market data...',
      
      // 85-100%: Monthly projections
      GENERATING_PROJECTIONS: 'Generating monthly projections...',
    }
  },
  DASHBOARD: {
    id: 'dashboard',
    weight: 1,
    statuses: {
      SUMMARY: 'Preparing dashboard summary...',
      CHARTS: 'Loading portfolio charts...',
      ACTIVITY: 'Getting recent activity...',
    }
  }
} as const;
